import { useAuth } from "@/_core/hooks/useAuth";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { trpc } from "@/lib/trpc";
import { TrendingUp, MessageSquare, Sparkles, ArrowUpRight, ArrowDownRight, RefreshCw, Search, Globe2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";

export default function Dashboard() {
  // Auth gating is handled by DashboardLayout; avoid hard redirects that can cause login loops.
  const { loading } = useAuth();
  const [isUpdating, setIsUpdating] = useState(false);
  const [aiReportDays, setAiReportDays] = useState<7 | 30 | 90>(30);

  // 獲取所有關鍵字用於更新和計算趨勢
  const { data: allKeywords = [] } = trpc.keywords.getAll.useQuery();

  // 計算 TOP5 上升關鍵字的 ID 列表
  const risingKeywordIds = allKeywords
    .filter((k: any) => (k.growthRateWoW || 0) > 0)
    .sort((a: any, b: any) => (b.growthRateWoW || 0) - (a.growthRateWoW || 0))
    .slice(0, 5)
    .map((k: any) => k.id);

  // 只查詢 TOP5 上升關鍵字的 PAA
  const { data: relatedIntents, isLoading: intentsLoading } = trpc.dashboard.getTopRelatedIntents.useQuery(
    { keywordIds: risingKeywordIds, limit: 100 },
    { enabled: risingKeywordIds.length > 0 }
  );
  const { data: aiPerformanceReport, isLoading: aiReportLoading } = trpc.dashboard.getAiPerformanceReport.useQuery({
    days: aiReportDays,
  });
  const updateSearchData = trpc.analytics.updateSearchData.useMutation();


  // Process data for Top Movers using allKeywords
  const risingKeywords = allKeywords
    .filter((k: any) => (k.growthRateWoW || 0) > 0)
    .sort((a: any, b: any) => (b.growthRateWoW || 0) - (a.growthRateWoW || 0))
    .slice(0, 5);

  const droppingKeywords = allKeywords
    .filter((k: any) => (k.growthRateWoW || 0) < 0)
    .sort((a: any, b: any) => (a.growthRateWoW || 0) - (b.growthRateWoW || 0))
    .slice(0, 5);

  const utils = trpc.useContext();

  const handleUpdateSearchData = async () => {
    if (risingKeywords.length === 0) {
      toast.error("尚無上升趨勢關鍵字，請先前往『關鍵字管理』頁面更新趨勢數據");
      return;
    }

    setIsUpdating(true);
    const keywordsToScan = risingKeywords.map((k: any) => k.keyword);
    toast.info(`正在針對 Top ${keywordsToScan.length} 上升關鍵字掃描 PAA...`);

    try {
      const response = await updateSearchData.mutateAsync({ keywords: keywordsToScan });

      const successResults = response.results.filter(r => r.success);
      const failedResults = response.results.filter(r => !r.success);
      const totalPaa = successResults.reduce((sum, r) => sum + (r.paaCount || 0), 0);

      if (failedResults.length > 0) {
        console.error('[Dashboard] Failed results:', failedResults);
      }

      if (successResults.length > 0) {
        toast.success(`✅ 更新完成！發現 ${totalPaa} 個 PAA 問題`);

        await Promise.all([
          utils.dashboard.getStats.invalidate(),
          utils.keywords.getAll.invalidate(),
          utils.dashboard.getTopRelatedIntents.invalidate(),
        ]);
      } else {
        toast.warning("沒有找到新的搜尋數據");
      }
    } catch (error: any) {
      console.error('[Dashboard] Error:', error);
      console.error('[Dashboard] Error stack:', error.stack);
      toast.error(`搜尋數據更新失敗: ${error.message || '未知錯誤'}`);
    } finally {
      setIsUpdating(false);
    }
  };

  const aiTrendData = (aiPerformanceReport?.trend || []).map((point: any) => ({
    ...point,
    dateLabel: point.date?.slice(5).replace("-", "/"),
  }));

  if (loading) {
    return <DashboardLayout><div className="p-8">載入中...</div></DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <div className="container py-8 space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold mb-2">數據儀表板</h1>
            <p className="text-muted-foreground">聚焦 AI 引用與搜尋趨勢重點</p>
          </div>
        </div>

        {/* AI Performance Report (Bing Webmaster 風格復刻) */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <CardTitle>AI 引用表現（預覽）</CardTitle>
                <CardDescription>
                  參考 Bing Webmaster AI Performance 報表，提供引用量、被引用關鍵字、頁面引用與可見度趨勢
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {[7, 30, 90].map((days) => (
                  <Button
                    key={days}
                    size="sm"
                    variant={aiReportDays === days ? "default" : "outline"}
                    onClick={() => setAiReportDays(days as 7 | 30 | 90)}
                  >
                    {days} 天
                  </Button>
                ))}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>被引用關鍵字數</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2">
                    <Search className="h-4 w-4 text-emerald-600" />
                    <div className="text-2xl font-bold">{aiPerformanceReport?.groundingQueries || 0}</div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>被引用頁面數</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2">
                    <Globe2 className="h-4 w-4 text-orange-600" />
                    <div className="text-2xl font-bold">{aiPerformanceReport?.citedPages || 0}</div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">可見度趨勢</CardTitle>
                <CardDescription>每日引用量與被引用頁面數</CardDescription>
              </CardHeader>
              <CardContent>
                {aiReportLoading ? (
                  <div className="text-sm text-muted-foreground">載入中...</div>
                ) : (
                  <ChartContainer
                    className="h-[260px] w-full"
                    config={{
                      citations: {
                        label: "引用次數",
                        color: "hsl(var(--chart-1))",
                      },
                      citedPages: {
                        label: "被引用頁數",
                        color: "hsl(var(--chart-2))",
                      },
                    }}
                  >
                    <LineChart data={aiTrendData}>
                      <CartesianGrid vertical={false} />
                      <XAxis dataKey="dateLabel" tickLine={false} axisLine={false} />
                      <YAxis allowDecimals={false} tickLine={false} axisLine={false} />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Line
                        type="monotone"
                        dataKey="citations"
                        stroke="var(--color-citations)"
                        strokeWidth={2}
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="citedPages"
                        stroke="var(--color-citedPages)"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ChartContainer>
                )}
              </CardContent>
            </Card>

            <div className="grid gap-4 lg:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">熱門被引用關鍵字</CardTitle>
                </CardHeader>
                <CardContent>
                  {aiPerformanceReport?.topQueries?.length ? (
                    <div className="space-y-2">
                      {aiPerformanceReport.topQueries.slice(0, 6).map((row: any) => (
                        <div key={row.keywordId} className="flex items-center justify-between text-sm border-b pb-2 last:border-b-0">
                          <span className="font-medium">{row.keyword}</span>
                          <span className="text-muted-foreground">{row.citations} citations</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground">目前沒有 query 資料</div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">頁面層級引用活動</CardTitle>
                </CardHeader>
                <CardContent>
                  {aiPerformanceReport?.topPages?.length ? (
                    <div className="space-y-2">
                      {aiPerformanceReport.topPages.slice(0, 6).map((row: any) => (
                        <div key={row.url} className="text-sm border-b pb-2 last:border-b-0">
                          <div className="flex items-center justify-between gap-2">
                            <a
                              href={row.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-medium hover:underline truncate"
                            >
                              {row.url}
                            </a>
                            <span className="text-muted-foreground shrink-0">{row.citations}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      目前沒有頁面引用資料（需在 `website_url` 已設定且來源 URL 命中該網域時才會顯示）
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </CardContent>
        </Card>

        {/* 關鍵字趨勢異動 (Top Movers) */}
        <div className="grid gap-6 md:grid-cols-2">
          {/* Rising Keywords */}
          <Card className="bg-accent/20 border-accent">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-accent-foreground">
                <TrendingUp className="h-5 w-5" />
                搜尋量上升
              </CardTitle>
              <CardDescription>
                本週成長幅度最大的關鍵字
              </CardDescription>
            </CardHeader>
            <CardContent>
              {risingKeywords.length > 0 ? (
                <div className="space-y-3">
                  {risingKeywords.map((keyword: any) => (
                    <div
                      key={keyword.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-background/80 border border-accent/30"
                    >
                      <span className="font-medium">{keyword.keyword}</span>
                      <span className="flex items-center font-bold text-sm text-green-600">
                        <ArrowUpRight className="h-4 w-4 mr-1" />
                        +{keyword.growthRateWoW?.toFixed(1)}%
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  尚無顯著上升趨勢
                </div>
              )}
            </CardContent>
          </Card>

          {/* Dropping Keywords */}
          <Card className="bg-secondary/20 border-secondary">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-secondary-foreground">
                <TrendingUp className="h-5 w-5 transform rotate-180" />
                搜尋量下降
              </CardTitle>
              <CardDescription>
                需注意的流量下滑關鍵字
              </CardDescription>
            </CardHeader>
            <CardContent>
              {droppingKeywords.length > 0 ? (
                <div className="space-y-3">
                  {droppingKeywords.map((keyword: any) => (
                    <div
                      key={keyword.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-background/80 border border-secondary/30"
                    >
                      <span className="font-medium">{keyword.keyword}</span>
                      <span className="flex items-center font-bold text-sm text-red-600">
                        <ArrowDownRight className="h-4 w-4 mr-1" />
                        {keyword.growthRateWoW?.toFixed(1)}%
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  尚無顯著下降趨勢
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* 關聯意圖 (User Questions) */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>使用者關注點 (User Questions)</CardTitle>
                <CardDescription>從 PAA (People Also Ask) 挖掘出的高頻問題</CardDescription>
              </div>
              <Button
                onClick={handleUpdateSearchData}
                disabled={isUpdating || risingKeywords.length === 0}
                variant="outline"
                size="sm"
                className="gap-2"
              >
                <RefreshCw className={`h-4 w-4 ${isUpdating ? "animate-spin" : ""}`} />
                更新搜尋數據
              </Button>
            </div>
          </CardHeader>
          <CardContent>


            {intentsLoading ? (
              <div className="text-center py-8 text-muted-foreground">載入中...</div>
            ) : relatedIntents && relatedIntents.length > 0 && risingKeywords.length > 0 ? (
              <Tabs defaultValue={String(risingKeywords[0].id)} className="w-full">
                <TabsList className="w-full justify-start overflow-x-auto">
                  {risingKeywords.map((keyword: any) => (
                    <TabsTrigger key={keyword.id} value={String(keyword.id)}>
                      {keyword.keyword}
                    </TabsTrigger>
                  ))}
                </TabsList>
                {risingKeywords.map((keyword: any) => {
                  const keywordIntents = relatedIntents.filter((intent: any) => intent.keywordId === keyword.id);
                  return (
                    <TabsContent key={keyword.id} value={String(keyword.id)} className="mt-4">
                      {keywordIntents.length > 0 ? (
                        <div className="grid gap-4 md:grid-cols-2">
                          {keywordIntents.map((intent: any) => (
                            <div key={intent.id} className="flex items-start gap-3 p-4 border rounded-lg hover:bg-accent/50 transition-colors group">
                              <MessageSquare className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm mb-1">{intent.question}</div>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <span className="bg-muted px-1.5 py-0.5 rounded">頻率: {intent.frequency}</span>
                                  <span>來源: {intent.source}</span>
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8 text-muted-foreground"
                                disabled
                                title="功能已停用"
                              >
                                <Sparkles className="h-4 w-4" />
                              </Button>
                            </div>))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-muted-foreground border border-dashed rounded-lg">
                          此關鍵字尚無 PAA 數據，請點擊上方更新按鈕
                        </div>
                      )}
                    </TabsContent>
                  );
                })}
              </Tabs>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center bg-muted/10 rounded-lg border border-dashed">
                <MessageSquare className="h-12 w-12 text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium mb-2">尚無關聯意圖數據</h3>
                <p className="text-muted-foreground max-w-sm mb-6">
                  點擊上方「更新數據」按鈕，系統將自動掃描 TOP5 上升關鍵字的 PAA。
                </p>
                <Button onClick={handleUpdateSearchData} disabled={isUpdating}>
                  {isUpdating ? "正在更新..." : "立即更新搜尋數據"}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

      </div>
    </DashboardLayout>
  );
}
